import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { BookOpen, ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { SEO } from "../components/SEO";
import { useLang } from "../i18n/LanguageContext";

/* Japon Zarafeti — siyah zeminde saga/sola akan dergi-usulu gorsel nehirleri + altin kanji seridi (Ritz-Carlton/Bentley hissi). */

const KANJI = [
  { k: "健康", romaji: "Kenkō", meaning: { tr: "Sağlık", en: "Health" } },
  { k: "善", romaji: "Zen", meaning: { tr: "İyilik", en: "Goodness" } },
  { k: "癒", romaji: "Iyashi", meaning: { tr: "Şifa", en: "Healing" } },
  { k: "和", romaji: "Wa", meaning: { tr: "Uyum", en: "Harmony" } },
  { k: "命", romaji: "Inochi", meaning: { tr: "Yaşam", en: "Life" } },
  { k: "心", romaji: "Kokoro", meaning: { tr: "Kalp", en: "Heart" } },
  { k: "絆", romaji: "Kizuna", meaning: { tr: "Bağ", en: "Bond" } },
  { k: "未来", romaji: "Mirai", meaning: { tr: "Gelecek", en: "Future" } },
  { k: "道", romaji: "Dō", meaning: { tr: "Yol", en: "The Way" } },
  { k: "美", romaji: "Bi", meaning: { tr: "Güzellik", en: "Beauty" } },
];

const RIVER_1 = ["/img/zarafet/onsen.jpg", "/img/zarafet/torii.jpg", "/img/zarafet/garden.jpg", "/img/zarafet/sakura.jpg", "/img/clinic/c1.jpg", "/img/clinic/c5.jpg"];
const RIVER_2 = ["/img/zarafet/tea.jpg", "/img/zarafet/tatami.jpg", "/img/zarafet/kyoto.jpg", "/img/zarafet/kintsugi.jpg", "/img/clinic/c3.jpg", "/img/clinic/c2.jpg"];

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.9 } };

function River({ images, kanji, dir, speed, testid }) {
  return (
    <motion.div {...fadeUp} className="relative py-8 sm:py-12" data-testid={testid}>
      <span aria-hidden className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-magserif text-[38vw] lg:text-[22vw] leading-none text-[#C9A96A] opacity-[0.08] select-none pointer-events-none">{kanji}</span>
      <Marquee speed={speed} direction={dir} gradient={false} pauseOnHover>
        {images.map((src, i) => (
          <div key={`${src}-${i}`} className="mx-3 sm:mx-4 rounded-2xl p-[2px] bg-[linear-gradient(140deg,#C9A96A_0%,#6e5522_45%,#C9A96A_65%,#8a6b2f_100%)] shadow-[0_28px_60px_-18px_rgba(0,0,0,0.85)]">
            <img src={src} alt="" loading="lazy" draggable={false} className="h-52 sm:h-72 lg:h-80 w-auto rounded-[calc(1rem-2px)] object-cover select-none" />
          </div>
        ))}
      </Marquee>
    </motion.div>
  );
}

export default function ZarafetPage() {
  const { lang } = useLang();
  const loc = (m) => m[lang] || m.tr;

  return (
    <div className="min-h-screen bg-[#0A0508] overflow-x-clip" data-testid="kanji-page">
      <SEO title="Japon Zarafeti — Sağlık, İyilik, Uyum" description="2D Healthcare'in ilham aldığı Japon değerleri: sağlık, iyilik, şifa, uyum. Akan görsel nehirler ve altın kanji." path="/zarafet" />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-10 text-center">
        <span aria-hidden className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-magserif text-[60vw] lg:text-[34vw] leading-none text-[#C9A96A] opacity-[0.05] select-none pointer-events-none">健康</span>
        <motion.p {...fadeUp} className="relative text-[11px] font-semibold tracking-[0.4em] text-[#C9A96A] uppercase">2D Healthcare × Japan</motion.p>
        <motion.h1 {...fadeUp} transition={{ duration: 0.9, delay: 0.1 }} className="relative mt-6 font-serif text-5xl sm:text-7xl font-bold tracking-tight text-[#F5EFE6]">
          {lang === "tr" ? "Japon Zarafeti" : "Japanese Grace"}
        </motion.h1>
        <motion.p {...fadeUp} transition={{ duration: 0.9, delay: 0.2 }} className="relative mt-6 max-w-xl mx-auto text-[#E8DCC8]/70 text-lg leading-relaxed px-6">
          {lang === "tr"
            ? "Bin yıllık bir estetik, akan bir nehir gibi: sağlık, iyilik ve şifa — hepsi tek bir fırça darbesinde."
            : "A thousand-year-old aesthetic flows like a river: health, goodness and healing in a single brushstroke."}
        </motion.p>
      </section>

      {/* Akan kanji seridi (sola) */}
      <motion.div {...fadeUp} className="relative py-6 border-y border-[#C9A96A]/15" data-testid="kanji-river">
        <Marquee speed={38} direction="left" gradient={false}>
          {KANJI.map((item) => (
            <div key={item.k} className="mx-8 sm:mx-12 flex items-center gap-5 select-none">
              <span className="font-magserif text-6xl sm:text-8xl text-[#C9A96A] leading-none">{item.k}</span>
              <span className="flex flex-col">
                <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-[#C9A96A]/60">{item.romaji}</span>
                <span className="font-serif text-2xl sm:text-3xl font-bold text-[#F5EFE6]">{loc(item.meaning)}</span>
              </span>
            </div>
          ))}
        </Marquee>
      </motion.div>

      {/* Gorsel nehir 1 (saga) */}
      <River images={RIVER_1} kanji="美" dir="right" speed={30} testid="river-1" />

      {/* Ara soz */}
      <motion.p {...fadeUp} className="max-w-2xl mx-auto text-center px-6 py-8 font-magserif italic text-xl sm:text-2xl text-[#C9A96A]/85 leading-relaxed">
        {lang === "tr" ? "“Şifa, acele etmeyenlerin sanatıdır.”" : "“Healing is the art of those who do not hurry.”"}
      </motion.p>

      {/* Gorsel nehir 2 (saga, daha yavas) */}
      <River images={RIVER_2} kanji="心" dir="right" speed={22} testid="river-2" />

      {/* Kapanis */}
      <section className="relative text-center py-24 px-6">
        <motion.div {...fadeUp}>
          <div className="font-magserif text-7xl text-[#C9A96A]">道</div>
          <p className="mt-6 max-w-md mx-auto text-[#E8DCC8]/70 leading-relaxed">
            {lang === "tr" ? "Sağlığa giden yol, zarafetle yüründüğünde daha insanıdır." : "The road to health is more human when walked with grace."}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link data-testid="kanji-to-tech" to="/teknoloji" className="inline-flex items-center gap-2.5 h-12 px-8 rounded-full bg-[#C9A96A] text-[#1d0810] font-heading font-bold text-sm tracking-wide hover:bg-[#dfc08a] transition-colors">
              {lang === "tr" ? "İleri Teknoloji" : "Advanced Technology"} <ArrowRight size={16} />
            </Link>
            <Link data-testid="kanji-to-magazine" to="/#dergi" className="inline-flex items-center gap-2.5 h-12 px-8 rounded-full border border-[#C9A96A]/40 text-[#C9A96A] font-heading font-semibold text-sm hover:bg-[#C9A96A]/10 transition-colors">
              <BookOpen size={16} /> {lang === "tr" ? "Dergimizi Açın" : "Open Our Magazine"}
            </Link>
            <Link data-testid="kanji-to-home" to="/" className="inline-flex items-center gap-2 text-sm text-[#C9A96A]/60 hover:text-[#C9A96A] transition-colors">
              <ArrowLeft size={15} /> {lang === "tr" ? "Ana Sayfa" : "Home"}
            </Link>
          </div>
        </motion.div>
      </section>
      <Footer />
    </div>
  );
}
